/** 與後端 CommerceLocaleHelper / name_locales JSON 鍵一致（不含 en，en 使用主欄位 name） */
export const COMMERCE_NAME_LOCALE_EXTRA_KEYS = [
  { jsonKey: 'zh-TC', formPath: ['nameLocales', 'zh-TC'] },
  { jsonKey: 'zh-SC', formPath: ['nameLocales', 'zh-SC'] },
];

/** 商品描述：主欄位 description 對應 JSON en；繁／簡與名稱相同鍵名 */
export const COMMERCE_DESCRIPTION_LOCALE_EXTRA_KEYS = [
  { jsonKey: 'zh-TC', formPath: ['descriptionLocales', 'zh-TC'] },
  { jsonKey: 'zh-SC', formPath: ['descriptionLocales', 'zh-SC'] },
];

/** 商品數量單位：主欄位 unitOfMeasure 對應 JSON en；繁／簡與名稱相同鍵名 */
export const COMMERCE_UNIT_OF_MEASURE_LOCALE_EXTRA_KEYS = [
  { jsonKey: 'zh-TC', formPath: ['unitOfMeasureLocales', 'zh-TC'] },
  { jsonKey: 'zh-SC', formPath: ['unitOfMeasureLocales', 'zh-SC'] },
];

/** 商品散裝單位：主欄位 unitOfBrokenPack 對應 JSON en；繁／簡與名稱相同鍵名 */
export const COMMERCE_UNIT_OF_BROKEN_PACK_LOCALE_EXTRA_KEYS = [
  { jsonKey: 'zh-TC', formPath: ['unitOfBrokenPackLocales', 'zh-TC'] },
  { jsonKey: 'zh-SC', formPath: ['unitOfBrokenPackLocales', 'zh-SC'] },
];

export function parseNameLocalesField(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw);
      return typeof o === 'object' && o != null ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function buildNameLocalesPayload(nameLocalesValues) {
  if (!nameLocalesValues || typeof nameLocalesValues !== 'object') return undefined;
  const out = {};
  for (const [k, v] of Object.entries(nameLocalesValues)) {
    if (v != null && String(v).trim()) out[k] = String(v).trim();
  }
  return Object.keys(out).length ? out : undefined;
}

const CUSTOMER_NAME_LINES = ['1', '2', '3'];

/** 客戶 name_locales：巢狀鍵 1/2/3 → zh-TC、zh-SC（en 用 name_1..3） */
export function emptyCustomerNameLocalesForm() {
  return {
    '1': { 'zh-TC': '', 'zh-SC': '' },
    '2': { 'zh-TC': '', 'zh-SC': '' },
    '3': { 'zh-TC': '', 'zh-SC': '' }
  };
}

export function parseCustomerNameLocalesRoot(raw) {
  const empty = emptyCustomerNameLocalesForm();
  const o = parseNameLocalesField(raw);
  for (const line of CUSTOMER_NAME_LINES) {
    const block = o[line];
    if (block && typeof block === 'object' && !Array.isArray(block)) {
      for (const loc of ['zh-TC', 'zh-SC']) {
        if (block[loc] != null) empty[line][loc] = String(block[loc]);
      }
    }
  }
  return empty;
}

export function buildCustomerNameLocalesPayload(formValue) {
  if (!formValue || typeof formValue !== 'object') return undefined;
  const out = {};
  for (const line of CUSTOMER_NAME_LINES) {
    const b = formValue[line];
    if (!b || typeof b !== 'object') continue;
    const inner = {};
    for (const loc of ['zh-TC', 'zh-SC']) {
      const v = b[loc];
      if (v != null && String(v).trim()) inner[loc] = String(v).trim();
    }
    if (Object.keys(inner).length) out[line] = inner;
  }
  return Object.keys(out).length ? out : undefined;
}

export function normalizeCustomerUiLocale(ui) {
  if (!ui || ui === 'en') return 'en';
  if (ui === 'zh-SC' || ui === 'zh-CN' || ui === 'zh_CN') return 'zh-SC';
  return 'zh-TC';
}

/** 與後端 CommerceLocaleHelper.NormalizeUiLocale 一致（空白視為 en） */
export function normalizeCommerceUiLocale(ui) {
  if (ui == null || String(ui).trim() === '') return 'en';
  const x = String(ui).trim();
  if (/^zh-TC$/i.test(x) || /^zh-TW$/i.test(x) || /^zh_TW$/i.test(x)) return 'zh-TC';
  if (/^zh-SC$/i.test(x) || /^zh-CN$/i.test(x) || /^zh_CN$/i.test(x)) return 'zh-SC';
  if (/^en$/i.test(x)) return 'en';
  return 'en';
}

/**
 * 商品／集合單一 name + 扁平 name_locales（en、zh-TC、zh-SC）；與 CommerceLocaleHelper.ResolveLocalizedName 一致。
 */
export function resolveLocalizedEntityName(defaultName, nameLocalesRaw, uiLocale) {
  const target = normalizeCommerceUiLocale(uiLocale);
  const map = parseNameLocalesField(nameLocalesRaw);
  const tryGet = (key) => {
    if (key == null) return '';
    for (const [k, v] of Object.entries(map)) {
      if (String(k).toLowerCase() === String(key).toLowerCase() && v != null && String(v).trim()) {
        return String(v).trim();
      }
    }
    return '';
  };
  const vTarget = tryGet(target);
  if (vTarget) return vTarget;
  if (target !== 'en') {
    const vEn = tryGet('en');
    if (vEn) return vEn;
  }
  const en = (defaultName ?? '').trim();
  return en;
}

/** 訂單快照僅有 CompanyNameCn / CompanyNameEn 時，依介面語系挑選顯示字串 */
export function pickOrderCustomerNameSnapshot(companyNameCn, companyNameEn, uiLocale) {
  const loc = normalizeCommerceUiLocale(uiLocale);
  const cn = (companyNameCn ?? '').trim();
  const en = (companyNameEn ?? '').trim();
  if (loc === 'en') return en || cn || '';
  return cn || en || '';
}

export function resolveCustomerLine(namePrimary, nameLocalesRoot, lineKey, uiLocale) {
  const loc = normalizeCustomerUiLocale(uiLocale);
  const en = (namePrimary ?? '').trim();
  if (loc === 'en') return en;
  const block = nameLocalesRoot?.[lineKey];
  const tc = block?.['zh-TC'] != null ? String(block['zh-TC']).trim() : '';
  const sc = block?.['zh-SC'] != null ? String(block['zh-SC']).trim() : '';
  if (loc === 'zh-SC') return sc || tc || en;
  if (loc === 'zh-TC') return tc || en;
  return en;
}

export function resolveCustomerDisplayName(name1, name2, name3, nameLocalesRoot, uiLocale, joiner = ' ') {
  const prim = [name1, name2, name3];
  const parts = CUSTOMER_NAME_LINES.map((k, i) =>
    resolveCustomerLine(prim[i], nameLocalesRoot, k, uiLocale)
  ).filter(Boolean);
  return parts.join(joiner);
}
